import { debounce } from 'lodash-es';

export const DIRECTIVE_NAME = 'updContainsFocus';
const hasFocus = el => el.contains(document.activeElement);

/**
 * This directive tracks whether its element contains the currently focused element
 * ("contains" meaning itself or a child element). It takes a callback which gets
 * invoked whenever the element gains or loses "contained" focus.
 *
 * Usage:
 * <div upd-contains-focus="$ctrl.containsFocusChange($value)"></div>
 */
export function containsFocus($parse) {
  'ngInject';
  return {
    restrict: 'A',
    compile: ($element, attr) => {
      const fn = $parse(attr[DIRECTIVE_NAME]);
      let lastValue;

      return (scope, element) => {
        const el = element[0];

        // When focus changes between elements, there is a brief moment where nothing
        // is focused b/c the `blur` event on the previously focused element occurs
        // immediately before the `focus` event. That would translate to an extra,
        // unnecessary value emit that we can avoid using debounce.
        const callback = debounce(() => {
          const value = hasFocus(el);

          if (value !== lastValue) {
            lastValue = value;
            scope.$evalAsync(() => fn(scope, { $value: hasFocus(el) }));
          }
        });

        // Would have preferred using listening for `focusin``focusout`, but Firefox
        // doesn't support them. Instead, listen for `focus`/`blur` (must use capture
        // phase because they don't bubble).
        // https://developer.mozilla.org/en-US/docs/Web/Events/focus#Event_delegation
        el.addEventListener('focus', callback, true);
        el.addEventListener('blur', callback, true);
      };
    },
  };
}
